import React, { useEffect, useState, createContext, useContext } from 'react';
import { UserContext } from '../user_context/UserContext';
import { subscriptionSnapshotListener } from '../../repository/StripeBillingDAO';
import { useOrganizationContext } from '../organization_context/OrganizationContext';
import { UserStatus } from '../../components/routes/routing_statuses/RoutingStatuses';
import { useAuthContext } from '../auth/AuthContext';

const SubscriptionContext = createContext();

const SubscriptionStatuses = {
    ACTIVE: "active",
    COMPLETE: "complete",
    TRIALING: "trialing",
    PAST_DUE: "past_due",
    CANCELED: "canceled",
    INCOMPLETE: "incomplete",
    INCOMPLETE_EXPIRED: "incomplete_expired",
    TRIALING_WILL_CANCEL: "trialing_will_cancel",
    UNPAID: "unpaid"
}

export const SubscriptionProvider = ({ children }) => {
    const { user } = useAuthContext();
    const { firebaseUser } = useContext(UserContext);
    const { organization, subscribedUsers, organizationStripeInfo } = useOrganizationContext();
    const [subscription, setSubscription] = useState(UserStatus.UNINITIALIZED);
    const [seatsAvailable, setSeatsAvailable] = useState(0);
    const [subscriptionEnd, setSubscriptionEnd] = useState(null);
    const [subscriptionStatus, setSubscriptionStatus] = useState(null);
    const [hasActiveSubscription, setHasActiveSubscription] = useState(false);
    const [isSubscriptionLoading, setIsSubscriptionLoading] = useState(true);

    useEffect(() => {
        if (firebaseUser === UserStatus.UNINITIALIZED || 
            organization === UserStatus.UNINITIALIZED || 
            user === UserStatus.UNAUTHENTICATED) {
            return;
        }

        if (!firebaseUser || !organization || !organizationStripeInfo) {
            setSubscription(null);
            setIsSubscriptionLoading(false);
            return;
        }
        if (!organizationStripeInfo.stripe_customer_id) {
            setSubscription(null);
            setIsSubscriptionLoading(false);
            return;
        }
        setIsSubscriptionLoading(true);        
        const unsubscribe = subscriptionSnapshotListener(
            organizationStripeInfo.stripe_customer_id,
            (fetchedSubscription) => {
                if (!fetchedSubscription) setSubscription(null);
                else setSubscription(fetchedSubscription);
                setIsSubscriptionLoading(false);
            }
        );

        return () => {
            unsubscribe?.();
        };
    }, [firebaseUser, organization, organizationStripeInfo, user]);

    useEffect(() => {
        if (subscription && subscription !== UserStatus.UNINITIALIZED) {
            const purchasedSeats = subscription.quantity;
            const availableSeats = purchasedSeats - (subscribedUsers ? Object.keys(subscribedUsers).length : 0);
            setSeatsAvailable(availableSeats);

            const endDate = new Date(subscription.current_period_end * 1000).toLocaleString();
            setSubscriptionEnd(endDate);
            setSubscriptionStatus(subscription.status);
        } else {
            setSeatsAvailable(0);
            setSubscriptionEnd(null);
            if (subscription !== UserStatus.UNINITIALIZED) setSubscriptionStatus(null);
        }
    }, [subscription, subscribedUsers]);

    useEffect(() => {
        if (isSubscriptionLoading) return;
        if (!subscription || subscription === UserStatus.UNINITIALIZED || subscription === UserStatus.DOES_NOT_EXIST) {
            setHasActiveSubscription(false);
            return;
        }
        
        if (
            firebaseUser && firebaseUser !== UserStatus.UNINITIALIZED &&
            subscription && subscription !== UserStatus.UNINITIALIZED &&
            subscribedUsers
        ) {
            const userId = firebaseUser.uid;
            const quantity = subscription.quantity;
            const seatsAvailable = quantity > 0;
            const userExists = Object.keys(subscribedUsers).includes(userId);
            const subscriptionEndDate = new Date(subscription.current_period_end * 1000);
            const currentDate = new Date();
            const activeStatus = subscription.status === SubscriptionStatuses.ACTIVE || 
                               subscription.status === SubscriptionStatuses.TRIALING || 
                               subscription.status === SubscriptionStatuses.COMPLETE;
            
            // Set subscription active state
            const isActive = userExists && 
                          subscriptionEndDate > currentDate && 
                          activeStatus && 
                          seatsAvailable;
            
            setHasActiveSubscription(isActive);
        } else {
            setHasActiveSubscription(false);
        }
    }, [firebaseUser, subscription, subscribedUsers, isSubscriptionLoading]);

    // Reset all subscription data
    const resetSubscriptionContext = () => {
        setSubscription(UserStatus.UNINITIALIZED);
        setSeatsAvailable(0);
        setSubscriptionEnd(null);
        setSubscriptionStatus(null);
        setHasActiveSubscription(false);
        setIsSubscriptionLoading(false);
    };

    return (
        <SubscriptionContext.Provider
            value={{
                subscription,
                subscribedUsers,
                seatsAvailable,
                subscriptionEnd,
                subscriptionStatus,
                hasActiveSubscription,
                isSubscriptionLoading,
                resetSubscriptionContext
            }}
        >
            {children}
        </SubscriptionContext.Provider>
    );
};

export const useSubscriptionContext = () => {
    return useContext(SubscriptionContext);
};
